import type {
  StreamAssistantMessageArgs,
  StreamAssistantRecoveryArgs,
  StreamCompletion,
} from "~/components/domain/ai/assistant-stream-client";
import { streamAssistantMessage, streamAssistantRecovery } from "~/components/domain/ai/assistant-stream-client";
import type { ActionArtifactState, ActionSelectionPatch } from "~/components/domain/assistant/action-artifact-state";
import {
  applyActionItemOutcome,
  applyActionLifecycle,
  applyActionProgress,
  applyActionProposalCommit,
  emptyActionArtifactState,
  setActionControlError,
  stageActionSelection as stageSelection,
} from "~/components/domain/assistant/action-artifact-state";
import {
  type AssistantFeedback,
  type AssistantTurnAttachment,
  activityLabel,
  appendPhaseSegment,
  appendStreamingContent,
  appendThoughtSegment,
  appendToolCallSegment,
  applyStreamingContent,
  attachWorkOnTurnEnd,
  type ChatMessage,
  type MessageWork,
  resolveToolResultSegment,
  type TurnSegment,
} from "~/components/domain/assistant/assistant-chat-state";
import type { ReportArtifactState, WorkspaceDisposition } from "~/components/domain/assistant/report-artifact-state";
import {
  activeReportArtifact,
  applyReportCommit,
  buildActiveReportDescriptor,
  deriveWorkspaceDisposition,
  emptyReportArtifactState,
  retainReportArtifactDatasets,
} from "~/components/domain/assistant/report-artifact-state";
import type {
  ActionBinding,
  ActionDisplayedSelection,
  ActionDraftContext,
  ActionIntent,
  ActionProposalArtifact,
} from "~/shared/ai/assistant-action-artifact";
import { ACTION_BINDINGS_MAX_PER_TURN } from "~/shared/ai/assistant-action-artifact";
import {
  ASSISTANT_ATTACHMENT_MAX_FILE_BYTES,
  ASSISTANT_ATTACHMENT_MAX_FILES_PER_MESSAGE,
  ASSISTANT_ATTACHMENT_MAX_MESSAGE_BYTES,
  type AssistantAttachmentEnvelope,
  stableAssistantAttachmentDigestPayload,
} from "~/shared/ai/assistant-attachment-contract";
import type { AssistantEntrySurface } from "~/shared/ai/assistant-entry-surface";
import type { DebugPayload } from "~/shared/ai/assistant-event-contract";

export type AssistantDraftAttachment = {
  clientId: string;
  file: File;
  fileName: string;
  sizeBytes: number;
  status: "uploading" | "attached" | "failed";
  sourceId?: string;
  error?: string;
  retryable?: boolean;
  previewUrl?: string;
};

export interface AssistantConversationState {
  messages: ChatMessage[];
  artifactState: ReportArtifactState;
  actionState: ActionArtifactState;
  panelArtifactKey: string | null;
  panelActionProposalId: string | null;
  panelOpened: boolean;
  debugPayloads: DebugPayload[];
  queryError: string | null;
  workspaceDisposition: WorkspaceDisposition | null;
  commitRejected: boolean;
  isStreaming: boolean;
  isLoading: boolean;
  turnSegments: TurnSegment[];
  contentStartedAfterSegmentCount: number | null;
  lastUserMessage: string;
  currentConversationId: string | null;
  draft: string;
  actionDraftContext: ActionDraftContext | null;
  draftId: string;
  draftAttachments: AssistantDraftAttachment[];
}

export interface AssistantConversationActions {
  submitQuery: (query: string, surface: AssistantEntrySurface, action?: { intent: ActionIntent }) => Promise<void>;
  retryRecovery: (surface: AssistantEntrySurface) => Promise<void>;
  stop: () => Promise<void>;
  clear: () => void;
  clearDebug: () => void;
  setPanelArtifactKey: (artifactKey: string | null) => void;
  setPanelOpened: (opened: boolean) => void;
  closePanel: () => void;
  showLatestArtifact: () => void;
  setPanelActionProposalId: (proposalId: string | null) => void;
  stageActionSelection: (proposalId: string, patch: ActionSelectionPatch) => void;
  submitFeedback: (turnId: string, feedback: AssistantFeedback | null, surface: AssistantEntrySurface) => Promise<void>;
  setDraft: (draft: string) => void;
  setActionDraftContext: (context: ActionDraftContext | null) => void;
  addAttachments: (files: File[]) => Promise<void>;
  removeAttachment: (clientId: string) => void;
  retryAttachment: (clientId: string) => Promise<void>;
}

export interface AssistantConversationStore extends AssistantConversationActions {
  getSnapshot: () => AssistantConversationState;
  subscribe: (listener: () => void) => () => void;
  dispose: () => void;
}

interface AssistantConversationStoreConfig {
  orgId: string;
  userId?: string;
  isSuperAdmin: boolean;
  selfName: string;
}

interface AssistantConversationStoreDependencies {
  streamMessage?: (args: StreamAssistantMessageArgs) => Promise<boolean>;
  streamRecovery?: (args: StreamAssistantRecoveryArgs) => Promise<boolean>;
  sendFeedback?: (args: {
    conversationId: string;
    clientTurnId: string;
    feedback: AssistantFeedback | null;
    surface: AssistantEntrySurface;
  }) => Promise<boolean>;
  createTurnId?: () => string;
  createDraftId?: () => string;
  now?: () => number;
  rehydrateActions?: (conversationId: string) => Promise<unknown[]>;
  recoverExactAction?: (pointer: AssistantRecoveryPointer, surface: AssistantEntrySurface) => Promise<unknown[]>;
  delay?: (milliseconds: number) => Promise<void>;
  uploadAttachment?: (args: {
    draftId: string;
    file: File;
    checksumSha256: string;
    sourceId?: string;
    onSourceReserved?: (sourceId: string) => boolean;
  }) => Promise<{ sourceId: string }>;
}

type FrozenSubmission = {
  query: string;
  turnId: string;
  newThread: boolean;
  draftId: string;
  attachments: AssistantDraftAttachment[];
  envelope?: AssistantAttachmentEnvelope;
  actionDraftContext: ActionDraftContext | null;
};

export interface AssistantRecoveryPointer {
  version: 1;
  conversationId: string;
  sourceTurnId: string;
  proposalId?: string;
}

interface RehydratedActionState {
  state: ActionArtifactState;
  proposalIds: string[];
}

interface DeferredRecoveryRequest {
  ownerSubmissionId: number;
  pointer: AssistantRecoveryPointer;
  surface: AssistantEntrySurface;
}

const parseRehydratedActions = (rawArtifacts: unknown[], orgId: string): RehydratedActionState => {
  let recovered = emptyActionArtifactState();
  const proposalIds: string[] = [];
  for (const rawArtifact of rawArtifacts) {
    const applied = applyActionProposalCommit(recovered, rawArtifact, orgId);
    recovered = applied.state;
    if (applied.proposalId) {
      proposalIds.push(applied.proposalId);
    }
  }
  return { state: recovered, proposalIds };
};

const actionBindingForArtifact = (artifact: ActionProposalArtifact): ActionBinding[] =>
  artifact.binding ? [{ proposalId: artifact.proposalId, version: artifact.version, token: artifact.binding.token }] : [];

const actionBindingsForTurn = (
  actionState: ActionArtifactState,
  surface: AssistantEntrySurface,
  intent?: ActionIntent,
): ActionBinding[] => {
  if (surface !== "assistant_page") {
    return [];
  }
  if (intent) {
    return [{ proposalId: intent.proposalId, version: intent.version, token: intent.token }];
  }
  return Object.values(actionState.artifacts)
    .filter(artifact => (artifact.status === "PENDING" || artifact.status === "APPLYING") && artifact.binding)
    .sort((left, right) => {
      const leftPriority = left.status === "PENDING" && left.counts.selected > 0 ? 0 : 1;
      const rightPriority = right.status === "PENDING" && right.counts.selected > 0 ? 0 : 1;
      if (leftPriority !== rightPriority) {
        return leftPriority - rightPriority;
      }
      const preparedDifference = Date.parse(right.preparedAt) - Date.parse(left.preparedAt);
      return preparedDifference !== 0 ? preparedDifference : left.proposalId.localeCompare(right.proposalId);
    })
    .flatMap(actionBindingForArtifact)
    .slice(0, ACTION_BINDINGS_MAX_PER_TURN);
};

const initialState = (draftId = crypto.randomUUID()): AssistantConversationState => ({
  messages: [],
  artifactState: emptyReportArtifactState(),
  actionState: emptyActionArtifactState(),
  panelArtifactKey: null,
  panelActionProposalId: null,
  panelOpened: false,
  debugPayloads: [],
  queryError: null,
  workspaceDisposition: null,
  commitRejected: false,
  isStreaming: false,
  isLoading: false,
  turnSegments: [],
  contentStartedAfterSegmentCount: null,
  lastUserMessage: "",
  currentConversationId: null,
  draft: "",
  actionDraftContext: null,
  draftId,
  draftAttachments: [],
});

const sendAssistantFeedback = async (args: {
  conversationId: string;
  clientTurnId: string;
  feedback: AssistantFeedback | null;
  surface: AssistantEntrySurface;
}): Promise<boolean> => {
  const response = await fetch("/api/rest/assistant-feedback", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(args),
  });
  return response.ok;
};

const fetchExactRecoveredAction = async (pointer: AssistantRecoveryPointer, surface: AssistantEntrySurface): Promise<unknown[]> => {
  const query = new URLSearchParams({
    conversationId: pointer.conversationId,
    sourceTurnId: pointer.sourceTurnId,
    surface,
    ...(pointer.proposalId ? { proposalId: pointer.proposalId } : {}),
  });
  const response = await fetch(`/api/rest/assistant-action-proposals?${query.toString()}`);
  if (!response.ok) {
    throw new Error("Action recovery request failed.");
  }
  const body: unknown = await response.json();
  if (typeof body !== "object" || body === null || !("artifacts" in body) || !Array.isArray(body.artifacts)) {
    throw new Error("Action recovery response was malformed.");
  }
  return body.artifacts;
};

const parseRecoveryPointer = (value: string | null): AssistantRecoveryPointer | null => {
  if (!value) {
    return null;
  }
  try {
    const parsed: unknown = JSON.parse(value);
    if (
      typeof parsed !== "object" ||
      parsed === null ||
      Array.isArray(parsed) ||
      !("version" in parsed) ||
      parsed.version !== 1 ||
      !("conversationId" in parsed) ||
      typeof parsed.conversationId !== "string" ||
      !("sourceTurnId" in parsed) ||
      typeof parsed.sourceTurnId !== "string" ||
      ("proposalId" in parsed && parsed.proposalId !== undefined && typeof parsed.proposalId !== "string")
    ) {
      return null;
    }
    const proposalId = "proposalId" in parsed ? parsed.proposalId : undefined;
    return {
      version: 1,
      conversationId: parsed.conversationId,
      sourceTurnId: parsed.sourceTurnId,
      ...(typeof proposalId === "string" ? { proposalId } : {}),
    };
  } catch {
    return null;
  }
};

const waitForRecoveryDelay = (milliseconds: number): Promise<void> =>
  new Promise(resolve => {
    setTimeout(resolve, milliseconds);
  });

const attachmentContentType = (file: File): string => {
  const extension = file.name.toLowerCase().split(".").pop() ?? "";
  const byExtension: Record<string, string> = {
    pdf: "application/pdf",
    png: "image/png",
    jpg: "image/jpeg",
    jpeg: "image/jpeg",
    webp: "image/webp",
    csv: "text/csv",
    xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    txt: "text/plain",
    md: "text/markdown",
  };
  return byExtension[extension] ?? file.type;
};

const supportedAssistantAttachmentExtensions = new Set(["pdf", "png", "jpg", "jpeg", "webp", "csv", "xlsx", "txt", "md"]);

const unsupportedAssistantAttachmentMessage = (fileName: string): string | null => {
  const extension = fileName.toLowerCase().split(".").pop() ?? "";
  if (supportedAssistantAttachmentExtensions.has(extension)) {
    return null;
  }
  if (["doc", "docx", "ppt", "pptx"].includes(extension)) {
    return "Save this file as PDF, then attach it again.";
  }
  if (extension === "xls") {
    return "Save this spreadsheet as .xlsx, then attach it again.";
  }
  return "Use PDF, PNG, JPEG, WebP, CSV, XLSX, TXT, or Markdown.";
};

const sha256File = async (file: File): Promise<string> => {
  const digest = await crypto.subtle.digest("SHA-256", await file.arrayBuffer());
  return [...new Uint8Array(digest)].map(byte => byte.toString(16).padStart(2, "0")).join("");
};

const uploadAssistantAttachment = async ({
  draftId,
  file,
  checksumSha256,
  sourceId,
  onSourceReserved,
}: {
  draftId: string;
  file: File;
  checksumSha256: string;
  sourceId?: string;
  onSourceReserved?: (sourceId: string) => boolean;
}): Promise<{ sourceId: string }> => {
  const grantResponse = await fetch("/api/rest/assistant-attachments", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      action: sourceId ? "resume" : "grant",
      draftId,
      ...(sourceId
        ? { sourceId }
        : {
            fileName: file.name,
            contentType: attachmentContentType(file),
            sizeBytes: file.size,
            checksumSha256,
          }),
    }),
  });
  const grantBody: unknown = await grantResponse.json();
  if (!grantResponse.ok || typeof grantBody !== "object" || grantBody === null) {
    const message =
      typeof grantBody === "object" && grantBody !== null && "error" in grantBody && typeof grantBody.error === "string"
        ? grantBody.error
        : "Upload could not be started.";
    throw new Error(message);
  }
  if ("alreadyComplete" in grantBody && grantBody.alreadyComplete === true && "id" in grantBody && typeof grantBody.id === "string") {
    if (onSourceReserved?.(grantBody.id) === false) {
      throw new Error("Upload was removed before it started.");
    }
    return { sourceId: grantBody.id };
  }
  if (!("signedUrl" in grantBody) || !("headers" in grantBody) || !("id" in grantBody) || typeof grantBody.id !== "string") {
    throw new Error("Upload grant was malformed.");
  }
  if (onSourceReserved?.(grantBody.id) === false) {
    throw new Error("Upload was removed before it started.");
  }
  if (typeof grantBody.signedUrl !== "string" || typeof grantBody.headers !== "object" || grantBody.headers === null) {
    throw new Error("Upload grant was malformed.");
  }
  const headers = new Headers();
  for (const [name, value] of Object.entries(grantBody.headers)) {
    if (typeof value !== "string") {
      throw new Error("Upload grant was malformed.");
    }
    headers.set(name, value);
  }
  const uploadResponse = await fetch(grantBody.signedUrl, { method: "PUT", headers, body: file });
  if (!uploadResponse.ok && !(sourceId && uploadResponse.status === 412)) {
    throw new Error("Upload failed. Try this file again.");
  }
  const completeResponse = await fetch("/api/rest/assistant-attachments", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ action: "complete", draftId, sourceId: grantBody.id }),
  });
  const completeBody: unknown = await completeResponse.json();
  if (!completeResponse.ok) {
    const message =
      typeof completeBody === "object" && completeBody !== null && "error" in completeBody && typeof completeBody.error === "string"
        ? completeBody.error
        : "Upload could not be verified.";
    throw new Error(message);
  }
  return { sourceId: grantBody.id };
};

const sha256Text = async (value: string): Promise<string> => {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(value));
  return [...new Uint8Array(digest)].map(byte => byte.toString(16).padStart(2, "0")).join("");
};

/** Framework-free owner for one authenticated browser-scope conversation. */
export const createAssistantConversationStore = (
  config: AssistantConversationStoreConfig,
  dependencies: AssistantConversationStoreDependencies = {},
): AssistantConversationStore => {
  const listeners = new Set<() => void>();
  const streamMessage = dependencies.streamMessage ?? streamAssistantMessage;
  const streamRecovery = dependencies.streamRecovery ?? streamAssistantRecovery;
  const sendFeedback = dependencies.sendFeedback ?? sendAssistantFeedback;
  const createTurnId = dependencies.createTurnId ?? (() => crypto.randomUUID());
  const createDraftId = dependencies.createDraftId ?? (() => crypto.randomUUID());
  const uploadAttachment = dependencies.uploadAttachment ?? uploadAssistantAttachment;
  const now = dependencies.now ?? Date.now;
  const fallbackRehydrateActions = dependencies.rehydrateActions;
  const recoverExactAction: (pointer: AssistantRecoveryPointer, surface: AssistantEntrySurface) => Promise<unknown[]> =
    dependencies.recoverExactAction ??
    (fallbackRehydrateActions
      ? (pointer: AssistantRecoveryPointer, _surface: AssistantEntrySurface) => fallbackRehydrateActions(pointer.conversationId)
      : fetchExactRecoveredAction);
  const delay = dependencies.delay ?? waitForRecoveryDelay;
  let state = initialState(createDraftId());
  let abortController: AbortController | null = null;
  let disposed = false;
  let submissionId = 0;
  let submissionInFlight = false;
  let recoveryInFlight = false;
  let startNewThread = true;
  let streamingContent = "";
  let nextActivityStepId = 0;
  let nextTurnSegmentId = 0;
  let turnStartedAt = 0;
  let receiptRetainUntil = 0;
  let inFlightRecoveryPointer: AssistantRecoveryPointer | null = null;
  let deferredRecoveryRequest: DeferredRecoveryRequest | null = null;
  let activeSubmission: FrozenSubmission | null = null;
  let retrySubmission: FrozenSubmission | null = null;
  const pendingRetirements = new Map<string, { conversationId: string | null; draftId: string }>();
  const ownedPreviewUrls = new Set<string>();
  const sessionKey = `assistant:active-conversation:${config.orgId}:${config.userId ?? "unknown"}`;

  const sessionStorageValue = (): string | null => {
    try {
      return typeof sessionStorage === "undefined" ? null : sessionStorage.getItem(sessionKey);
    } catch {
      return null;
    }
  };

  let activeRecoveryPointer = parseRecoveryPointer(sessionStorageValue());

  const setSessionPointer = (pointer: AssistantRecoveryPointer | null): void => {
    activeRecoveryPointer = pointer;
    try {
      if (typeof sessionStorage === "undefined") {
        return;
      }
      if (pointer) {
        sessionStorage.setItem(sessionKey, JSON.stringify(pointer));
      } else {
        sessionStorage.removeItem(sessionKey);
      }
    } catch {
      // Session retention is a convenience; chat and approval remain server-authoritative.
    }
  };

  const sameRecoveryOwner = (expected: AssistantRecoveryPointer, current: AssistantRecoveryPointer): boolean =>
    expected.conversationId === current.conversationId &&
    expected.sourceTurnId === current.sourceTurnId &&
    (expected.proposalId === undefined || expected.proposalId === current.proposalId);

  const deferRecoveryUntilSubmissionReleases = (surface: AssistantEntrySurface): void => {
    if (!activeRecoveryPointer) {
      return;
    }
    const existing = deferredRecoveryRequest;
    if (existing?.ownerSubmissionId === submissionId) {
      if (!sameRecoveryOwner(existing.pointer, activeRecoveryPointer)) {
        // This call was made after the active turn committed a different proposal. Replace the
        // obsolete request with the newly rendered target; without this fresh call, finalization
        // still rejects the old request against the changed recovery pointer.
        deferredRecoveryRequest = {
          ownerSubmissionId: submissionId,
          pointer: { ...activeRecoveryPointer },
          surface,
        };
        return;
      }
      deferredRecoveryRequest = {
        ...existing,
        pointer: { ...activeRecoveryPointer },
        surface: existing.surface === "assistant_page" || surface === "assistant_page" ? "assistant_page" : "ask_ai_popover",
      };
      return;
    }
    deferredRecoveryRequest = {
      ownerSubmissionId: submissionId,
      pointer: { ...activeRecoveryPointer },
      surface,
    };
  };

  const takeDeferredRecovery = (ownerSubmissionId: number): DeferredRecoveryRequest | null => {
    const request = deferredRecoveryRequest;
    deferredRecoveryRequest = null;
    if (
      !request ||
      request.ownerSubmissionId !== ownerSubmissionId ||
      !activeRecoveryPointer ||
      !sameRecoveryOwner(request.pointer, activeRecoveryPointer)
    ) {
      return null;
    }
    return request;
  };

  const syncSessionRetention = (pointer: AssistantRecoveryPointer, actionState: ActionArtifactState): void => {
    const active = Object.values(actionState.artifacts).some(artifact => artifact.status === "PENDING" || artifact.status === "APPLYING");
    setSessionPointer(active || now() <= receiptRetainUntil ? pointer : null);
  };

  const retainRecoveredTerminalReceipt = (proposalId: string | undefined, actionState: ActionArtifactState): void => {
    const artifact = proposalId ? actionState.artifacts[proposalId] : undefined;
    if (artifact && artifact.status !== "PENDING" && artifact.status !== "APPLYING") {
      receiptRetainUntil = now() + 15 * 60 * 1000;
    }
  };

  const publish = (updater: (current: AssistantConversationState) => AssistantConversationState) => {
    if (disposed) {
      return;
    }
    state = updater(state);
    listeners.forEach(listener => listener());
  };

  const discardDraftSource = async (draftId: string, sourceId: string): Promise<boolean> => {
    try {
      const response = await fetch("/api/rest/assistant-attachments", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "discard", draftId, sourceId }),
      });
      return response.ok;
    } catch {
      return false;
    }
  };

  const createImagePreview = (file: File): string | undefined => {
    if (!attachmentContentType(file).startsWith("image/") || typeof URL.createObjectURL !== "function") {
      return undefined;
    }
    try {
      const previewUrl = URL.createObjectURL(file);
      ownedPreviewUrls.add(previewUrl);
      return previewUrl;
    } catch {
      return undefined;
    }
  };

  const releaseImagePreviews = (): void => {
    if (typeof URL.revokeObjectURL === "function") {
      ownedPreviewUrls.forEach(previewUrl => URL.revokeObjectURL(previewUrl));
    }
    ownedPreviewUrls.clear();
  };

  const conversationHasAttachmentContext = (): boolean => state.messages.some(message => (message.attachments?.length ?? 0) > 0);

  const uploadDraftAttachment = async (clientId: string, draftId: string, file: File, existingSourceId?: string): Promise<void> => {
    try {
      const checksumSha256 = await sha256File(file);
      const uploaded = await uploadAttachment({
        draftId,
        file,
        checksumSha256,
        sourceId: existingSourceId,
        onSourceReserved: sourceId => {
          const attachmentStillPresent =
            !disposed && state.draftId === draftId && state.draftAttachments.some(attachment => attachment.clientId === clientId);
          if (!attachmentStillPresent) {
            void discardDraftSource(draftId, sourceId);
            return false;
          }
          publish(value => ({
            ...value,
            draftAttachments: value.draftAttachments.map(attachment =>
              attachment.clientId === clientId ? { ...attachment, sourceId } : attachment,
            ),
          }));
          return true;
        },
      });
      const attachmentStillPresent =
        state.draftId === draftId && state.draftAttachments.some(attachment => attachment.clientId === clientId);
      if (!attachmentStillPresent) {
        void fetch("/api/rest/assistant-attachments", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ action: "discard", draftId, sourceId: uploaded.sourceId }),
        });
        return;
      }
      publish(value =>
        value.draftId !== draftId
          ? value
          : {
              ...value,
              draftAttachments: value.draftAttachments.map(attachment =>
                attachment.clientId === clientId
                  ? { ...attachment, status: "attached", sourceId: uploaded.sourceId, error: undefined }
                  : attachment,
              ),
            },
      );
    } catch (error) {
      const message =
        error instanceof TypeError
          ? "Upload could not reach storage. Try this file again."
          : error instanceof Error
            ? error.message
            : "Upload failed.";
      publish(value =>
        value.draftId !== draftId
          ? value
          : {
              ...value,
              draftAttachments: value.draftAttachments.map(attachment =>
                attachment.clientId === clientId ? { ...attachment, status: "failed", error: message } : attachment,
              ),
            },
      );
    }
  };

  const retireConversation = async (target: { conversationId: string | null; draftId: string }, reportFailure: boolean): Promise<void> => {
    const targetKey = `${target.conversationId ?? "draft"}:${target.draftId}`;
    try {
      const response = await fetch("/api/rest/assistant-conversation-retire", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(target),
      });
      if (!response.ok) {
        throw new Error("Conversation retirement failed.");
      }
      if (pendingRetirements.delete(targetKey)) {
        publish(value => ({ ...value, queryError: null }));
      }
    } catch {
      pendingRetirements.set(targetKey, target);
      if (reportFailure) {
        publish(value => ({ ...value, queryError: "The prior conversation could not be retired yet. Retry to finish clearing it." }));
      }
    }
  };

  const addAttachments = async (files: File[]): Promise<void> => {
    if (disposed || files.length === 0) {
      return;
    }
    const remaining = ASSISTANT_ATTACHMENT_MAX_FILES_PER_MESSAGE - state.draftAttachments.length;
    const accepted = files.slice(0, Math.max(0, remaining));
    const totalBytes = state.draftAttachments.reduce((total, attachment) => total + attachment.sizeBytes, 0);
    let admittedBytes = totalBytes;
    const draftId = state.draftId;
    const newAttachments: AssistantDraftAttachment[] = [];
    for (const file of accepted) {
      const unsupportedMessage = unsupportedAssistantAttachmentMessage(file.name);
      if (unsupportedMessage) {
        newAttachments.push({
          clientId: crypto.randomUUID(),
          file,
          fileName: file.name,
          sizeBytes: file.size,
          status: "failed",
          error: unsupportedMessage,
          retryable: false,
          previewUrl: createImagePreview(file),
        });
        continue;
      }
      if (
        file.size < 1 ||
        file.size > ASSISTANT_ATTACHMENT_MAX_FILE_BYTES ||
        admittedBytes + file.size > ASSISTANT_ATTACHMENT_MAX_MESSAGE_BYTES
      ) {
        newAttachments.push({
          clientId: crypto.randomUUID(),
          file,
          fileName: file.name,
          sizeBytes: file.size,
          status: "failed",
          retryable: false,
          error:
            file.size > ASSISTANT_ATTACHMENT_MAX_FILE_BYTES
              ? "Files must be 15 MiB or smaller."
              : "Attachments exceed the 45 MiB message limit.",
        });
        continue;
      }
      admittedBytes += file.size;
      newAttachments.push({
        clientId: crypto.randomUUID(),
        file,
        fileName: file.name,
        sizeBytes: file.size,
        status: "uploading",
        previewUrl: createImagePreview(file),
      });
    }
    if (files.length > remaining) {
      publish(value => ({ ...value, queryError: "You can attach up to five files to one message." }));
    }
    publish(value => ({ ...value, draftAttachments: [...value.draftAttachments, ...newAttachments] }));
    await Promise.all(
      newAttachments
        .filter(attachment => attachment.status === "uploading")
        .map(attachment => uploadDraftAttachment(attachment.clientId, draftId, attachment.file)),
    );
  };

  const retryAttachment = async (clientId: string): Promise<void> => {
    const attachment = state.draftAttachments.find(candidate => candidate.clientId === clientId);
    if (!attachment || attachment.status !== "failed" || attachment.retryable === false) {
      return;
    }
    const draftId = state.draftId;
    publish(value => ({
      ...value,
      draftAttachments: value.draftAttachments.map(candidate =>
        candidate.clientId === clientId ? { ...candidate, status: "uploading", error: undefined } : candidate,
      ),
    }));
    await uploadDraftAttachment(clientId, draftId, attachment.file, attachment.sourceId);
  };

  const retirementTargets = (): Array<{ conversationId: string | null; draftId: string }> => {
    const targets = new Map<string, { conversationId: string | null; draftId: string }>();
    const currentTarget = { conversationId: state.currentConversationId, draftId: state.draftId };
    targets.set(`${currentTarget.conversationId ?? "draft"}:${currentTarget.draftId}`, currentTarget);
    if (activeSubmission && activeSubmission.draftId !== state.draftId) {
      const submittedTarget = {
        conversationId: state.currentConversationId ?? activeSubmission.envelope?.expectedConversationId ?? null,
        draftId: activeSubmission.draftId,
      };
      targets.set(`${submittedTarget.conversationId ?? "draft"}:${submittedTarget.draftId}`, submittedTarget);
    }
    return [...targets.values()];
  };

  const resetConversation = () => {
    const retiringTargets = retirementTargets();
    abortController?.abort();
    abortController = null;
    inFlightRecoveryPointer = null;
    submissionInFlight = false;
    recoveryInFlight = false;
    submissionId += 1;
    startNewThread = true;
    streamingContent = "";
    nextActivityStepId = 0;
    nextTurnSegmentId = 0;
    receiptRetainUntil = 0;
    deferredRecoveryRequest = null;
    setSessionPointer(null);
    retrySubmission = null;
    activeSubmission = null;
    publish(() => initialState(createDraftId()));
    releaseImagePreviews();
    retiringTargets.forEach(target => void retireConversation(target, true));
  };

  /** A failed provider turn retires its server conversation, so no visible state may imply that
   * the next request still has that context. Keep only the prompt needed by the explicit Retry. */
  const retireVisibleConversation = (retryMessage = "", queryError: string | null = null) => {
    startNewThread = true;
    streamingContent = "";
    nextActivityStepId = 0;
    nextTurnSegmentId = 0;
    publish(() => ({ ...initialState(createDraftId()), lastUserMessage: retryMessage, queryError }));
  };

  const recoverActiveActions = async (
    pointer: AssistantRecoveryPointer,
    surface: AssistantEntrySurface,
    retryMessage: string,
    streamError: string | null,
  ): Promise<void> => {
    const recoverySubmissionId = submissionId;
    recoveryInFlight = true;
    publish(value => ({
      ...value,
      isLoading: true,
      isStreaming: false,
      turnSegments: [],
      contentStartedAfterSegmentCount: null,
      queryError: streamError,
      lastUserMessage: retryMessage,
    }));
    const recoveryDelays = [0, 100, 500, 1500, 4000];
    try {
      for (const recoveryDelay of recoveryDelays) {
        if (recoveryDelay > 0) {
          await delay(recoveryDelay);
        }
        if (disposed || submissionId !== recoverySubmissionId) {
          return;
        }
        try {
          const rawArtifacts = await recoverExactAction(pointer, surface);
          const recovered = parseRehydratedActions(rawArtifacts, config.orgId);
          if (recovered.proposalIds.length === 0) {
            continue;
          }
          const leasePending = Object.values(recovered.state.artifacts).some(
            artifact => artifact.status === "APPLYING" && !artifact.resumable,
          );
          if (leasePending) {
            continue;
          }
          const proposalId = pointer.proposalId ?? recovered.proposalIds[0];
          const pinnedPointer = proposalId ? { ...pointer, proposalId } : pointer;
          retainRecoveredTerminalReceipt(proposalId, recovered.state);
          startNewThread = false;
          publish(value => ({
            ...value,
            actionState: recovered.state,
            currentConversationId: pointer.conversationId,
            isLoading: false,
            isStreaming: false,
            queryError: streamError,
          }));
          syncSessionRetention(pinnedPointer, recovered.state);
          return;
        } catch {
          // A transient rehydration failure must not erase durable action context.
        }
      }
      if (disposed || submissionId !== recoverySubmissionId) {
        return;
      }
      startNewThread = false;
      const recoveryError = "I couldn't confirm whether your changes were saved. Reload the page to check.";
      publish(value => ({
        ...value,
        isLoading: false,
        isStreaming: false,
        queryError: streamError ? `${streamError} ${recoveryError}` : recoveryError,
      }));
    } finally {
      if (submissionId === recoverySubmissionId) {
        recoveryInFlight = false;
      }
    }
  };

  const settleTurn = (attachment?: AssistantTurnAttachment) => {
    const work: MessageWork = {
      segments: state.turnSegments,
      contentStartedAfterSegmentCount: state.contentStartedAfterSegmentCount,
      durationMs: now() - turnStartedAt,
    };
    publish(value => ({
      ...value,
      messages: attachWorkOnTurnEnd(value.messages, work, attachment),
      turnSegments: [],
      contentStartedAfterSegmentCount: null,
      isLoading: false,
      isStreaming: false,
    }));
    streamingContent = "";
  };

  const completeTurn = (completion: StreamCompletion): AssistantTurnAttachment => {
    const applied = applyReportCommit(
      state.artifactState,
      completion.reportArtifactCommit,
      config.orgId,
      state.panelOpened ? state.panelArtifactKey : null,
    );
    const rejected = Boolean(applied.rejectionReason);
    const actionApplied = applyActionProposalCommit(state.actionState, completion.actionProposalCommit, config.orgId);
    const actionRejected = Boolean(actionApplied.rejectionReason);
    if (
      actionApplied.proposalId &&
      actionApplied.state.artifacts[actionApplied.proposalId]?.status !== "PENDING" &&
      actionApplied.state.artifacts[actionApplied.proposalId]?.status !== "APPLYING"
    ) {
      receiptRetainUntil = now() + 15 * 60 * 1000;
    }
    const disposition = deriveWorkspaceDisposition({
      phase: rejected ? "failure" : "success",
      hasArtifact: activeReportArtifact(applied.state) !== null,
      commitApplied: applied.applied,
    });
    publish(value => ({
      ...value,
      artifactState: applied.state,
      actionState: actionApplied.state,
      panelArtifactKey: !value.panelOpened && applied.applied ? applied.state.activeArtifactKey : value.panelArtifactKey,
      commitRejected: rejected || actionRejected,
      workspaceDisposition: disposition,
      queryError: null,
      currentConversationId: completion.conversationId,
    }));
    const retainedProposalPointer = actionApplied.proposalId
      ? activeRecoveryPointer?.proposalId === actionApplied.proposalId
        ? activeRecoveryPointer
        : inFlightRecoveryPointer?.proposalId === actionApplied.proposalId
          ? inFlightRecoveryPointer
          : null
      : null;
    const sourceTurnId = retainedProposalPointer?.sourceTurnId ?? completion.turnId ?? activeRecoveryPointer?.sourceTurnId;
    if (sourceTurnId) {
      const completionPointer: AssistantRecoveryPointer = actionApplied.proposalId
        ? {
            version: 1,
            conversationId: completion.conversationId,
            sourceTurnId,
            proposalId: actionApplied.proposalId,
          }
        : activeRecoveryPointer?.proposalId
          ? activeRecoveryPointer
          : { version: 1, conversationId: completion.conversationId, sourceTurnId };
      syncSessionRetention(completionPointer, actionApplied.state);
    }
    inFlightRecoveryPointer = null;
    return {
      ...(applied.artifactKey ? { artifactKey: applied.artifactKey } : {}),
      ...(actionApplied.proposalId ? { actionProposalId: actionApplied.proposalId } : {}),
      ...(disposition ? { disposition } : {}),
      commitRejected: rejected || actionRejected,
      conversationId: completion.conversationId,
      turnId: completion.turnId,
    };
  };

  const retryRecovery = async (surface: AssistantEntrySurface): Promise<void> => {
    if (pendingRetirements.size > 0 && !recoveryInFlight && !submissionInFlight && !disposed) {
      await Promise.all([...pendingRetirements.values()].map(target => retireConversation(target, true)));
      return;
    }
    if (!activeRecoveryPointer || recoveryInFlight || disposed) {
      return;
    }
    if (submissionInFlight) {
      deferRecoveryUntilSubmissionReleases(surface);
      return;
    }
    deferredRecoveryRequest = null;
    const pointer = activeRecoveryPointer;
    recoveryInFlight = true;
    submissionId += 1;
    const recoverySubmissionId = submissionId;
    const controller = new AbortController();
    abortController = controller;
    streamingContent = "";
    turnStartedAt = now();
    const turnId = createTurnId();
    let recoveryError = state.queryError;
    publish(value => ({ ...value, isLoading: true, isStreaming: true, queryError: null }));
    try {
      await streamRecovery({
        providerRecovery: {
          conversationId: pointer.conversationId,
          sourceTurnId: pointer.sourceTurnId,
          ...(pointer.proposalId ? { proposalId: pointer.proposalId } : {}),
        },
        turnId,
        surface,
        signal: controller.signal,
        callbacks: {
          onContent: text => {
            if (disposed || submissionId !== recoverySubmissionId) {
              return;
            }
            const content = appendStreamingContent(streamingContent, text);
            const needsAssistantMessage = streamingContent === "";
            streamingContent = content;
            publish(value => ({
              ...value,
              messages: applyStreamingContent(
                needsAssistantMessage ? [...value.messages, { role: "assistant", content: "" }] : value.messages,
                content,
              ),
              isLoading: false,
            }));
          },
          onComplete: completion => {
            if (!disposed && submissionId === recoverySubmissionId) {
              recoveryError = null;
              settleTurn(completeTurn(completion));
            }
          },
          onError: message => {
            if (!disposed && submissionId === recoverySubmissionId) {
              recoveryError = message;
              publish(value => ({ ...value, isLoading: false, isStreaming: false, queryError: message }));
            }
          },
        },
      });
    } finally {
      if (submissionId === recoverySubmissionId) {
        recoveryInFlight = false;
        abortController = null;
      }
    }
    if (!disposed && submissionId === recoverySubmissionId) {
      await recoverActiveActions(pointer, surface, state.lastUserMessage, recoveryError);
    }
  };

  const submitQuery = async (rawQuery: string, surface: AssistantEntrySurface, action?: { intent: ActionIntent }) => {
    const query = rawQuery.trim();
    if ((!query && state.draftAttachments.length === 0) || submissionInFlight || recoveryInFlight || disposed) {
      return;
    }
    const submittedAttachments = state.draftAttachments;
    if (submittedAttachments.some(attachment => attachment.status !== "attached" || !attachment.sourceId)) {
      publish(value => ({ ...value, queryError: "Wait for every attachment to finish uploading, or retry/remove failed files." }));
      return;
    }
    const activeReportDescriptor = buildActiveReportDescriptor(state.artifactState);
    const actionBindings = actionBindingsForTurn(state.actionState, surface, action?.intent);
    const displayedSelections: ActionDisplayedSelection[] = actionBindings.flatMap(binding => {
      const patch = state.actionState.stagedSelectionPatches[binding.proposalId];
      if (!patch || (patch.includeItemKeys.length === 0 && patch.excludeItemKeys.length === 0)) {
        return [];
      }
      const artifact = state.actionState.artifacts[binding.proposalId];
      const selectedItemKeys =
        patch.selectedItemKeys ??
        artifact?.inlineItems
          ?.filter(
            item =>
              item.eligibility === "READY" &&
              (patch.includeItemKeys.includes(item.itemKey) || (item.selected && !patch.excludeItemKeys.includes(item.itemKey))),
          )
          .map(item => item.itemKey);
      return selectedItemKeys ? [{ ...binding, selectedItemKeys }] : [];
    });
    if (
      submittedAttachments.length > 0 &&
      (action !== undefined ||
        state.actionDraftContext !== null ||
        actionBindings.length > 0 ||
        activeRecoveryPointer?.proposalId !== undefined)
    ) {
      publish(value => ({
        ...value,
        queryError: "Send files in a new message.",
      }));
      return;
    }
    const submittedDraftId = state.draftId;
    const exactRetry =
      retrySubmission &&
      retrySubmission.query === query &&
      retrySubmission.draftId === submittedDraftId &&
      retrySubmission.attachments.length === submittedAttachments.length &&
      retrySubmission.attachments.every((attachment, index) => {
        const current = submittedAttachments[index];
        return (
          current !== undefined &&
          current.sourceId === attachment.sourceId &&
          current.fileName === attachment.fileName &&
          current.sizeBytes === attachment.sizeBytes &&
          current.status === attachment.status
        );
      })
        ? retrySubmission
        : null;
    submissionInFlight = true;
    submissionId += 1;
    const activeSubmissionId = submissionId;
    const controller = new AbortController();
    abortController = controller;
    streamingContent = "";
    nextActivityStepId = 0;
    nextTurnSegmentId = 0;
    turnStartedAt = now();
    const newThread = exactRetry?.newThread ?? startNewThread;
    startNewThread = false;
    const turnId = exactRetry?.turnId ?? createTurnId();
    let attachmentEnvelope = exactRetry?.envelope;
    if (!attachmentEnvelope && submittedAttachments.length > 0) {
      const sources = submittedAttachments.map((attachment, ordinal) => ({
        sourceId: attachment.sourceId ?? "",
        ordinal,
        fileName: attachment.fileName,
        sizeBytes: attachment.sizeBytes,
      }));
      const expectedConversationId = newThread ? null : state.currentConversationId;
      const submissionDigest = await sha256Text(
        stableAssistantAttachmentDigestPayload({ turnId, message: query, expectedConversationId, sources }),
      );
      attachmentEnvelope = { draftId: submittedDraftId, expectedConversationId, sources, submissionDigest };
    }
    if (disposed || submissionId !== activeSubmissionId || controller.signal.aborted) {
      return;
    }
    const submittedDraftContext = state.actionDraftContext;
    const submission: FrozenSubmission = {
      query,
      turnId,
      newThread,
      draftId: submittedDraftId,
      attachments: submittedAttachments,
      ...(attachmentEnvelope ? { envelope: attachmentEnvelope } : {}),
      actionDraftContext: submittedDraftContext,
    };
    activeSubmission = submission;
    retrySubmission = null;
    // A card press (save, don't save, resume, a pick, a checkbox change) is not something the person wrote: it posts no
    // message as them, leaves the composer as it is, and is not offered again as the last message. History still records it.
    const pressedProposalId = action?.intent.proposalId ?? null;
    if (pressedProposalId !== null) {
      publish(value => ({
        ...value,
        actionState: setActionControlError(value.actionState, pressedProposalId, null),
        turnSegments: [],
        contentStartedAfterSegmentCount: null,
        isLoading: true,
        isStreaming: true,
        queryError: null,
        workspaceDisposition: null,
        commitRejected: false,
      }));
    }
    publish(value =>
      pressedProposalId !== null
        ? value
        : {
            ...value,
            messages: [
              ...value.messages,
              {
                role: "user",
                content: query,
                ...(attachmentEnvelope
                  ? {
                      attachments: attachmentEnvelope.sources.map(source => ({
                        sourceId: source.sourceId,
                        fileName: source.fileName,
                        sizeBytes: source.sizeBytes,
                        previewUrl: submittedAttachments[source.ordinal]?.previewUrl,
                      })),
                    }
                  : {}),
              },
            ],
            turnSegments: [],
            contentStartedAfterSegmentCount: null,
            isLoading: true,
            isStreaming: true,
            queryError: null,
            workspaceDisposition: null,
            commitRejected: false,
            lastUserMessage: query,
            draft: "",
            draftId: createDraftId(),
            draftAttachments: [],
            actionDraftContext: null,
          },
    );
    const stillOwnsSubmission = () => !disposed && submissionId === activeSubmissionId;
    const restoreSubmittedDraft = (): void => {
      if (pressedProposalId !== null) {
        return;
      }
      publish(value => ({
        ...value,
        draft: submission.query,
        draftId: submission.draftId,
        draftAttachments: submission.attachments,
        actionDraftContext: submission.actionDraftContext,
      }));
    };
    let turnRecovery: Promise<void> | null = null;
    try {
      await streamMessage({
        message: query,
        newThread,
        turnId,
        surface,
        debugMode: config.isSuperAdmin,
        activeReportDescriptor,
        actionBindings,
        displayedSelections,
        actionIntent: action?.intent,
        actionDraftContext: submittedDraftContext ?? undefined,
        attachmentEnvelope,
        signal: controller.signal,
        callbacks: {
          onConversationReady: conversationId => {
            if (!stillOwnsSubmission()) {
              return;
            }
            const pointer: AssistantRecoveryPointer = { version: 1, conversationId, sourceTurnId: turnId };
            inFlightRecoveryPointer = pointer;
            if (!activeRecoveryPointer?.proposalId) {
              setSessionPointer(pointer);
            }
            publish(value => ({ ...value, currentConversationId: conversationId }));
          },
          onContent: text => {
            if (!stillOwnsSubmission()) {
              return;
            }
            const content = appendStreamingContent(streamingContent, text);
            const needsAssistantMessage = streamingContent === "";
            streamingContent = content;
            publish(value => ({
              ...value,
              messages: applyStreamingContent(
                needsAssistantMessage ? [...value.messages, { role: "assistant", content: "" }] : value.messages,
                content,
              ),
              contentStartedAfterSegmentCount: value.contentStartedAfterSegmentCount ?? value.turnSegments.length,
              isLoading: false,
            }));
          },
          onComplete: completion => {
            if (stillOwnsSubmission()) {
              retrySubmission = null;
              activeSubmission = null;
              settleTurn(completeTurn(completion));
              if (completion.completionDisposition === "recovery_only") {
                restoreSubmittedDraft();
              }
            }
          },
          onError: message => {
            if (!stillOwnsSubmission()) {
              return;
            }
            retrySubmission = submission.envelope ? submission : null;
            activeSubmission = null;
            if (pressedProposalId !== null) {
              // The failure shows on the card that was pressed; the card's state is refreshed without a thread error.
              const pressRecovery = inFlightRecoveryPointer ?? activeRecoveryPointer;
              const showOnCard = () =>
                publish(value => ({
                  ...value,
                  isLoading: false,
                  isStreaming: false,
                  actionState: setActionControlError(value.actionState, pressedProposalId, message),
                }));
              turnRecovery = pressRecovery
                ? recoverActiveActions(pressRecovery, surface, state.lastUserMessage, null).then(showOnCard)
                : Promise.resolve(showOnCard());
              void turnRecovery;
              return;
            }
            restoreSubmittedDraft();
            const matchingDurablePointer =
              submittedDraftContext && activeRecoveryPointer?.proposalId === submittedDraftContext.proposalId
                ? activeRecoveryPointer
                : null;
            const recoveryPointer = matchingDurablePointer ?? inFlightRecoveryPointer ?? activeRecoveryPointer;
            if (attachmentEnvelope || conversationHasAttachmentContext()) {
              if (state.currentConversationId === null) {
                startNewThread = true;
              }
              inFlightRecoveryPointer = null;
              setSessionPointer(null);
              publish(value => ({ ...value, isLoading: false, isStreaming: false, queryError: message }));
            } else if (recoveryPointer) {
              turnRecovery = recoverActiveActions(recoveryPointer, surface, query, message);
              void turnRecovery;
            } else {
              retireVisibleConversation(query, message);
            }
          },
          onDebug: payload => {
            if (stillOwnsSubmission()) {
              publish(value => ({ ...value, debugPayloads: [...value.debugPayloads, payload] }));
            }
          },
          onStatus: message => {
            if (!stillOwnsSubmission()) {
              return;
            }
            publish(value => {
              const next = appendThoughtSegment(
                value.turnSegments,
                message,
                nextTurnSegmentId,
                value.contentStartedAfterSegmentCount !== null,
              );
              if (next.length > value.turnSegments.length) {
                nextTurnSegmentId += 1;
              }
              return { ...value, turnSegments: next };
            });
          },
          onPhase: event => {
            if (stillOwnsSubmission()) {
              publish(value => ({ ...value, turnSegments: appendPhaseSegment(value.turnSegments, nextTurnSegmentId++, event) }));
            }
          },
          onToolCall: (name, toolArgs, callId) => {
            if (!stillOwnsSubmission()) {
              return;
            }
            const stepId = nextActivityStepId++;
            publish(value => {
              const next = appendToolCallSegment(
                value.turnSegments,
                { id: stepId, name, callId, ...activityLabel(name, toolArgs, { selfName: config.selfName }) },
                nextTurnSegmentId,
              );
              if (next.length > value.turnSegments.length) {
                nextTurnSegmentId += 1;
              }
              return { ...value, turnSegments: next };
            });
          },
          onToolResult: (name, outcome, _durationMs, callId) => {
            if (stillOwnsSubmission()) {
              publish(value => ({ ...value, turnSegments: resolveToolResultSegment(value.turnSegments, name, outcome, callId) }));
            }
          },
          onActionProgress: event => {
            if (stillOwnsSubmission()) {
              publish(value => ({ ...value, actionState: applyActionProgress(value.actionState, event) }));
            }
          },
          onActionItemOutcome: event => {
            if (stillOwnsSubmission()) {
              publish(value => ({ ...value, actionState: applyActionItemOutcome(value.actionState, event) }));
            }
          },
          onActionLifecycle: event => {
            if (stillOwnsSubmission()) {
              publish(value => {
                const actionState = applyActionLifecycle(value.actionState, event);
                if (event.status !== "PENDING" && event.status !== "APPLYING") {
                  receiptRetainUntil = now() + 15 * 60 * 1000;
                }
                if (activeRecoveryPointer) {
                  syncSessionRetention(activeRecoveryPointer, actionState);
                }
                return { ...value, actionState };
              });
            }
          },
        },
      });
      if (turnRecovery) {
        await turnRecovery;
      }
    } finally {
      if (submissionId === activeSubmissionId) {
        submissionInFlight = false;
        abortController = null;
        const deferredRecovery = takeDeferredRecovery(activeSubmissionId);
        if (deferredRecovery) {
          await retryRecovery(deferredRecovery.surface);
        }
      }
    }
  };

  const submitFeedback = async (turnId: string, feedback: AssistantFeedback | null, surface: AssistantEntrySurface) => {
    const message = state.messages.find(candidate => candidate.turnId === turnId && candidate.role === "assistant");
    if (!message?.conversationId || disposed) {
      return;
    }
    publish(value => ({
      ...value,
      messages: value.messages.map(candidate =>
        candidate.turnId === turnId ? { ...candidate, feedbackPending: true, feedbackError: undefined } : candidate,
      ),
    }));
    try {
      const saved = await sendFeedback({ conversationId: message.conversationId, clientTurnId: turnId, feedback, surface });
      if (!saved) {
        throw new Error("Feedback could not be saved.");
      }
      publish(value => ({
        ...value,
        messages: value.messages.map(candidate =>
          candidate.turnId === turnId
            ? { ...candidate, feedback: feedback ?? undefined, feedbackPending: false, feedbackError: undefined }
            : candidate,
        ),
      }));
    } catch (error) {
      publish(value => ({
        ...value,
        messages: value.messages.map(candidate =>
          candidate.turnId === turnId
            ? {
                ...candidate,
                feedbackPending: false,
                feedbackError: error instanceof Error ? error.message : "Feedback could not be saved.",
              }
            : candidate,
        ),
      }));
    }
  };

  const retainedPointer = activeRecoveryPointer;
  if (retainedPointer) {
    void recoverExactAction(retainedPointer, "ask_ai_popover")
      .then(rawArtifacts => {
        if (disposed || rawArtifacts.length === 0) {
          return;
        }
        const recovered = parseRehydratedActions(rawArtifacts, config.orgId);
        if (recovered.proposalIds.length === 0) {
          return;
        }
        const proposalId = retainedPointer.proposalId ?? recovered.proposalIds[0];
        if (proposalId) {
          retainRecoveredTerminalReceipt(proposalId, recovered.state);
          setSessionPointer({ ...retainedPointer, proposalId });
        }
        startNewThread = false;
        publish(value => ({
          ...value,
          currentConversationId: retainedPointer.conversationId,
          actionState: recovered.state,
          messages: recovered.proposalIds.map(proposalId => ({
            role: "assistant",
            content: "",
            actionProposalId: proposalId,
            conversationId: retainedPointer.conversationId,
          })),
        }));
      })
      .catch(() => {
        // Keep the retained conversation key so a reload can retry an unavailable route.
      });
  }

  return {
    getSnapshot: () => state,
    subscribe: listener => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    dispose: () => {
      if (disposed) {
        return;
      }
      const targets = retirementTargets();
      abortController?.abort();
      abortController = null;
      submissionId += 1;
      submissionInFlight = false;
      recoveryInFlight = false;
      deferredRecoveryRequest = null;
      state = initialState(createDraftId());
      disposed = true;
      releaseImagePreviews();
      targets.forEach(target => void retireConversation(target, false));
      listeners.forEach(listener => listener());
      listeners.clear();
    },
    submitQuery,
    retryRecovery,
    stop: async () => {
      const submittedDraft = activeSubmission?.envelope || conversationHasAttachmentContext() ? activeSubmission : null;
      retrySubmission = submittedDraft?.envelope ? submittedDraft : retrySubmission;
      activeSubmission = null;
      abortController?.abort();
      abortController = null;
      submissionInFlight = false;
      deferredRecoveryRequest = null;
      submissionId += 1;
      if (submittedDraft) {
        if (submittedDraft.newThread && state.currentConversationId === null) {
          startNewThread = true;
        }
        inFlightRecoveryPointer = null;
        setSessionPointer(null);
        publish(value => ({
          ...value,
          draft: submittedDraft.query,
          draftId: submittedDraft.draftId,
          draftAttachments: submittedDraft.attachments,
          actionDraftContext: submittedDraft.actionDraftContext,
          isLoading: false,
          isStreaming: false,
          queryError: null,
        }));
        return;
      }
      const recoveryPointer = inFlightRecoveryPointer ?? activeRecoveryPointer;
      if (recoveryPointer) {
        await recoverActiveActions(recoveryPointer, "ask_ai_popover", "", null);
      } else {
        retireVisibleConversation();
      }
    },
    clear: resetConversation,
    clearDebug: () => publish(value => ({ ...value, debugPayloads: [] })),
    setPanelArtifactKey: panelArtifactKey => publish(value => ({ ...value, panelArtifactKey })),
    setPanelActionProposalId: panelActionProposalId => publish(value => ({ ...value, panelActionProposalId })),
    stageActionSelection: (proposalId, patch) =>
      publish(value => ({ ...value, actionState: stageSelection(value.actionState, proposalId, patch) })),
    setPanelOpened: panelOpened => publish(value => ({ ...value, panelOpened })),
    closePanel: () =>
      publish(value => ({
        ...value,
        panelOpened: false,
        panelArtifactKey: value.artifactState.activeArtifactKey,
        artifactState: retainReportArtifactDatasets(value.artifactState, null),
      })),
    showLatestArtifact: () =>
      publish(value =>
        value.artifactState.activeArtifactKey
          ? {
              ...value,
              panelArtifactKey: value.artifactState.activeArtifactKey,
              artifactState: retainReportArtifactDatasets(value.artifactState, value.artifactState.activeArtifactKey),
            }
          : value,
      ),
    submitFeedback,
    setDraft: draft => publish(value => ({ ...value, draft })),
    setActionDraftContext: actionDraftContext => publish(value => ({ ...value, actionDraftContext })),
    addAttachments,
    removeAttachment: clientId => {
      const attachment = state.draftAttachments.find(candidate => candidate.clientId === clientId);
      const draftId = state.draftId;
      publish(value => ({ ...value, draftAttachments: value.draftAttachments.filter(candidate => candidate.clientId !== clientId) }));
      if (attachment?.sourceId) {
        void fetch("/api/rest/assistant-attachments", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ action: "discard", draftId, sourceId: attachment.sourceId }),
        }).then(response => {
          if (!response.ok) {
            publish(value => ({ ...value, queryError: `Could not remove ${attachment.fileName} from server storage yet.` }));
          }
        });
      }
    },
    retryAttachment,
  };
};
